import React, { createContext, useContext, useMemo } from 'react';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
// FIXME temporary import fix to avoid circular dependency - context folder should not depend on hooks folder
import type {
	FacebookShareRecommendations,
	RedditShareRecommendations,
	// eslint-disable-next-line @change/sibling-module-imports
} from 'src/app/pages/starterDashboardV2/shared/hooks/useFetchShareRecommendations';
// eslint-disable-next-line @change/sibling-module-imports
import { useShareRecommendations } from 'src/app/pages/starterDashboardV2/shared/hooks/useShareRecommendations';

export type Recommendation = NonNullable<FacebookShareRecommendations[0] | RedditShareRecommendations[0]>;

type RecommendationsContextType = {
	data: {
		facebook: FacebookShareRecommendations;
		reddit: RedditShareRecommendations;
		isLoading: boolean;
		hasData: boolean;
		hasFacebookGroups: boolean;
		hasRedditGroups: boolean;
		shareRecommendations: { facebook: FacebookShareRecommendations; reddit: RedditShareRecommendations };
		shouldDisplayShareRecommendations: boolean;
		shareRecommendationsLoading: boolean;
		isShareRecommendationsEnabled: boolean;
	};
	actions: {
		getFacebookGroups: () => FacebookShareRecommendations;
		getRedditGroups: () => RedditShareRecommendations;
	};
};

const RecommendationsContext = createContext<RecommendationsContextType>({
	data: {
		facebook: [],
		reddit: [],
		isLoading: false,
		hasData: false,
		hasFacebookGroups: false,
		hasRedditGroups: false,
		shareRecommendations: { facebook: [], reddit: [] },
		shouldDisplayShareRecommendations: false,
		shareRecommendationsLoading: false,
		isShareRecommendationsEnabled: false,
	},
	actions: {
		getFacebookGroups: () => [],
		getRedditGroups: () => [],
	},
});

export const useRecommendations = () => useContext(RecommendationsContext);

export const RecommendationsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const {
		data: {
			petition: { slug },
		},
	} = useStarterDashboardV2PageData();

	const shareRecommendationsResult = useShareRecommendations(slug);

	const contextValue = useMemo(() => {
		const {
			shareRecommendations,
			shouldDisplayShareRecommendations,
			shareRecommendationsLoading,
			isShareRecommendationsEnabled,
		} = shareRecommendationsResult.data;

		return {
			data: {
				facebook: shareRecommendations.facebook,
				reddit: shareRecommendations.reddit,
				isLoading: shareRecommendationsLoading,
				hasData: shouldDisplayShareRecommendations,
				hasFacebookGroups: shareRecommendations.facebook.length > 0,
				hasRedditGroups: shareRecommendations.reddit.length > 0,
				shareRecommendations,
				shouldDisplayShareRecommendations,
				shareRecommendationsLoading,
				isShareRecommendationsEnabled,
			},
			actions: {
				getFacebookGroups: () => shareRecommendations.facebook,
				getRedditGroups: () => shareRecommendations.reddit,
			},
		};
	}, [shareRecommendationsResult.data]);

	return <RecommendationsContext.Provider value={contextValue}>{children}</RecommendationsContext.Provider>;
};
